import { describePrice } from "@/features/booking/modules/session-types/utils/pricing";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/**
 * How the client screens say lengths, prices, days, and names. Days are counted on the
 * specialist's clock (the server does the same), so a 23:30 time never jumps a column because
 * the reader sits in another zone.
 */

const DAY_MS = 86_400_000;

/** 45 -> "45 min", 60 -> "1 h", 90 -> "1 h 30 min". */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return m.booking_portal_minutes({ minutes: rest });
  if (rest === 0) return m.booking_portal_hours({ hours });
  return m.booking_portal_hours_minutes({ hours, minutes: rest });
};

/** "2 places", in Polish "2 miejsca" or "5 miejsc": Polish counts 2–4 and 5+ differently. */
export const formatPlacesCount = (count: number): string => {
  const lastTwo = count % 100;
  const last = count % 10;
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
    ? m.booking_portal_places_few({ count })
    : m.booking_portal_places_many({ count });
};

const WEEK_MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];

/** "Mon" for 1, in the reader's language. 1 January 2024 was a Monday. */
const shortWeekday = (weekday: number) => {
  const name = new Intl.DateTimeFormat(getLocale(), { weekday: "short", timeZone: "UTC" })
    .format(new Date(Date.UTC(2024, 0, 1 + ((weekday + 6) % 7))))
    .replace(/\.$/, "");
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/** 480 -> "8:00", 1440 -> "24:00". */
const clock = (minute: number) => `${Math.floor(minute / 60)}:${String(minute % 60).padStart(2, "0")}`;

/**
 * A week of hours as a person says it: days with the same hours share a line, Monday first, and
 * three days or more in a row read as a span. ["Mon–Wed, Fri 8:00–19:00", "Sat 9:00–13:00"].
 */
export const formatWeekHours = (hours: { weekday: number; startMinute: number; endMinute: number }[]): string[] => {
  const groups = new Map<string, number[]>();
  for (const weekday of WEEK_MONDAY_FIRST) {
    const ranges = hours
      .filter((range) => range.weekday === weekday)
      .toSorted((a, b) => a.startMinute - b.startMinute)
      .map((range) => `${clock(range.startMinute)}–${clock(range.endMinute)}`)
      .join(", ");
    if (ranges) groups.set(ranges, [...(groups.get(ranges) ?? []), weekday]);
  }
  return [...groups].map(([ranges, days]) => {
    // Split the days into runs of neighbours, Monday first: Mon Tue Wed Fri -> [Mon Tue Wed] [Fri].
    const runs: number[][] = [];
    for (const day of days) {
      const run = runs.at(-1);
      const previous = run?.at(-1);
      if (run && previous !== undefined && WEEK_MONDAY_FIRST.indexOf(previous) === WEEK_MONDAY_FIRST.indexOf(day) - 1) {
        run.push(day);
      } else runs.push([day]);
    }
    const label = runs
      .map((run) =>
        run.length >= 3
          ? `${shortWeekday(run[0] ?? 0)}–${shortWeekday(run.at(-1) ?? 0)}`
          : run.map(shortWeekday).join(", "),
      )
      .join(", ");
    return `${label} ${ranges}`;
  });
};

/** "PLN 180", "From PLN 180", "Free", or null when the business keeps the price to itself. */
export const formatServicePrice = ({
  amount,
  from,
  currency,
}: {
  amount: number | null | undefined;
  from?: boolean;
  currency: string;
}): string | null => {
  const label = describePrice({ amount, from, currency, locale: getLocale() });
  if (label.kind === "none") return null;
  if (label.kind === "free") return m.booking_portal_free();
  return label.kind === "from" ? m.booking_portal_price_from({ price: label.price }) : label.price;
};

/** "2026-10-06" for the calendar day `value` falls on in `timeZone`. */
export const dayKeyIn = (value: string | Date, timeZone?: string): string =>
  new Intl.DateTimeFormat("en-CA", {
    ...(timeZone ? { timeZone } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));

const pad = (value: number) => String(value).padStart(2, "0");

/** Noon UTC on the day a key names: safe to format in UTC without the date slipping. */
const keyToDate = (key: string): Date => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1, 12));
};

export const addDaysToKey = (key: string, days: number): string => {
  const next = new Date(keyToDate(key).getTime() + days * DAY_MS);
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
};

/** A grid column's heading: "Today", "Tomorrow", or the short weekday, over "6 Oct". */
export const dayHeading = (key: string, todayKey: string): { title: string; date: string } => {
  const date = keyToDate(key);
  const locale = getLocale();
  const title =
    key === todayKey
      ? m.booking_portal_today()
      : key === addDaysToKey(todayKey, 1)
        ? m.booking_portal_tomorrow()
        : new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(date);
  return {
    title,
    date: new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }).format(date),
  };
};

/** "Sep 22": a past or future day without its weekday, for "Last visit Sep 22". */
export const formatShortDate = (value: string | Date, timeZone?: string): string =>
  new Intl.DateTimeFormat(getLocale(), { ...(timeZone ? { timeZone } : {}), day: "numeric", month: "short" }).format(
    new Date(value),
  );

/** "Tue, Oct 6, 2026" style for a visit long past, where the year matters. */
export const formatDateWithYear = (value: string | Date, timeZone?: string): string =>
  new Intl.DateTimeFormat(getLocale(), {
    ...(timeZone ? { timeZone } : {}),
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));

/** "Alex", "Alex and Sam", "Alex, Sam and Jo". */
export const formatNames = (names: string[]): string => {
  if (names.length <= 1) return names[0] ?? "";
  return m.booking_portal_names_and({ first: names.slice(0, -1).join(", "), last: names[names.length - 1] ?? "" });
};

export const firstNameOf = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** Whatever the SDK reports as an error (its own type in a real project): only a message is read. */
export const messageOf = (error: unknown): string =>
  error && typeof error === "object" && "message" in error && typeof error.message === "string" ? error.message : "";
